import React, { useEffect, useState } from "react";
import api from "../lib/api";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  TrendingUp,
  Globe2,
  Clock,
  ShieldCheck,
  ArrowUpRight,
} from "lucide-react";

const CHART_COLORS = ["#B33A3A", "#D9A05B", "#0F172A", "#64748B", "#94A3B8", "#F87171", "#CBD5E1"];

function fmtUSD(n) {
  if (!n) return "$0";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n}`;
}

function KPI({ label, value, sub, icon: Icon }) {
  return (
    <div className="bengal-card p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <span className="eyebrow">{label}</span>
        <Icon size={16} className="text-[#B33A3A]" strokeWidth={1.75} />
      </div>
      <div className="mt-3 text-3xl font-serif font-semibold tracking-tight text-slate-900 tabular-nums">
        {value}
      </div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/stats");
        setStats(data);
      } catch (e) {
        setErr("Failed to load dashboard");
      }
    })();
  }, []);

  if (err) return <div className="p-8 text-red-600">{err}</div>;
  if (!stats) return <div className="p-8 eyebrow text-slate-500">Loading…</div>;

  const { kpis, country_graph, category_graph, activity } = stats;

  return (
    <div className="p-6 md:p-8 fade-in-up" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="eyebrow">Workspace</div>
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight mt-1">
            Portfolio Overview
          </h1>
          <p className="text-slate-600 mt-2 text-sm md:text-base">
            Real-time view of your global IP assets, filings, and licensing activity.
          </p>
        </div>
        <div className="text-right">
          <div className="eyebrow">Estimated portfolio value</div>
          <div className="text-3xl font-mono font-semibold text-[#B33A3A]" data-testid="total-value">
            {fmtUSD(kpis.total_value_usd)}
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-8">
        <KPI label="Total Assets" value={kpis.total_assets} sub="Across all jurisdictions" icon={Globe2} />
        <KPI label="Granted / Registered" value={kpis.granted + kpis.registered} sub={`${kpis.pending} pending`} icon={ShieldCheck} />
        <KPI label="Renewals Due" value={kpis.renewals_due_180d} sub="Next 180 days" icon={Clock} />
        <KPI label="Portfolio Value" value={fmtUSD(kpis.total_value_usd)} sub="AI-estimated" icon={TrendingUp} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5 mb-8">
        {/* Country graph (stacked by type) — spans 2 */}
        <div className="bengal-card p-6 lg:col-span-2" data-testid="country-graph">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="eyebrow">Global IP Filings</div>
              <h3 className="font-serif text-2xl font-semibold tracking-tight mt-1">By Country & Type</h3>
            </div>
            <div className="flex items-center gap-4 text-xs">
              {[
                ["Patents", "#B33A3A"],
                ["Trademarks", "#D9A05B"],
                ["Copyrights", "#0F172A"],
              ].map(([l, c]) => (
                <div key={l} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: c }} />
                  <span className="text-slate-600">{l}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <BarChart data={country_graph} barCategoryGap={24}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="country" tick={{ fontSize: 12, fill: "#475569" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#475569" }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    borderRadius: 2,
                    border: "1px solid #E2E8F0",
                    fontSize: 12,
                    fontFamily: "IBM Plex Sans",
                  }}
                />
                <Bar dataKey="patent" stackId="a" fill="#B33A3A" />
                <Bar dataKey="trademark" stackId="a" fill="#D9A05B" />
                <Bar dataKey="copyright" stackId="a" fill="#0F172A" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category pie */}
        <div className="bengal-card p-6" data-testid="category-graph">
          <div className="mb-5">
            <div className="eyebrow">Categories</div>
            <h3 className="font-serif text-2xl font-semibold tracking-tight mt-1">By Domain</h3>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={category_graph}
                  dataKey="value"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  innerRadius={55}
                  paddingAngle={2}
                  stroke="#fff"
                  strokeWidth={2}
                >
                  {category_graph.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    borderRadius: 2,
                    border: "1px solid #E2E8F0",
                    fontSize: 12,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 space-y-1.5 max-h-[120px] overflow-auto">
            {category_graph.slice(0, 6).map((c, i) => (
              <div key={c.category} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <span className="text-slate-700 truncate">{c.category}</span>
                </div>
                <span className="font-mono text-slate-500">{c.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Activity + Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
        <div className="bengal-card p-6 lg:col-span-2" data-testid="activity-feed">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eyebrow">Recent Activity</div>
              <h3 className="font-serif text-2xl font-semibold tracking-tight mt-1">Latest Filings</h3>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {activity.map((a) => (
              <div key={a.id} className="py-3 flex items-center justify-between gap-4" data-testid={`activity-${a.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 truncate">{a.title}</div>
                  <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                    <span className="capitalize">{a.ip_type}</span>
                    <span>·</span>
                    <span>{a.country}</span>
                  </div>
                </div>
                <span
                  className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${
                    a.status === "granted" || a.status === "registered"
                      ? "bg-green-50 text-green-800 border-green-200"
                      : "bg-amber-50 text-amber-800 border-amber-200"
                  }`}
                >
                  {a.status}
                </span>
              </div>
            ))}
            {activity.length === 0 && <div className="text-sm text-slate-500 py-6">No activity yet.</div>}
          </div>
        </div>

        <div className="bengal-card p-6" data-testid="status-summary">
          <div className="eyebrow mb-3">Status Distribution</div>
          <div className="space-y-3">
            {[
              { label: "Granted", value: kpis.granted, color: "#16A34A" },
              { label: "Registered", value: kpis.registered, color: "#2563EB" },
              { label: "Pending", value: kpis.pending, color: "#D97706" },
            ].map((s) => {
              const pct = kpis.total_assets ? (s.value / kpis.total_assets) * 100 : 0;
              return (
                <div key={s.label}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-slate-700">{s.label}</span>
                    <span className="font-mono text-slate-500">{s.value}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-sm overflow-hidden">
                    <div style={{ width: `${pct}%`, background: s.color }} className="h-full transition-all duration-500" />
                  </div>
                </div>
              );
            })}
          </div>
          <a
            href="/portfolio"
            className="mt-6 flex items-center justify-between text-sm text-[#B33A3A] hover:underline group"
            data-testid="view-portfolio-link"
          >
            <span>View full portfolio</span>
            <ArrowUpRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
          </a>
        </div>
      </div>
    </div>
  );
}
